/**
 * S-3058 FIXER: Synchronous Pose Filtering
 * 
 * Lightweight filters that run SYNCHRONOUSLY in the render loop
 * to reduce jitter, accordion effects, and discontinuities.
 * 
 * Key Constraints:
 * - Must be synchronous (no async/await)
 * - Must be fast (<1ms per frame)
 * - Must not break requestVideoFrameCallback timing
 * - Must handle [[x,y,z,vis]] landmark format from V1
 */

interface LandmarkArray {
  [index: number]: number; // [x, y, z, visibility]
  length: number;
}

type LandmarksArray = LandmarkArray[]

/**
 * EMA (Exponential Moving Average) filter for smooth motion
 */
class EMAFilter {
  private history: Map<string, [number, number, number]> = new Map();
  private alpha: number;

  constructor(alpha: number = 0.3) {
    this.alpha = alpha; // 0.3 = more smoothing, 0.7 = more responsive
  }

  filter(landmarks: LandmarksArray): LandmarksArray {
    const filtered: LandmarksArray = [] as any;

    for (let i = 0; i < landmarks.length; i++) {
      const lm = landmarks[i];
      if (!lm || lm[0] === undefined) {
        filtered[i] = lm;
        continue;
      }

      const key = `${i}`;
      const prev = this.history.get(key);

      if (prev) {
        // Apply EMA: new = alpha * current + (1 - alpha) * previous
        const x = this.alpha * lm[0] + (1 - this.alpha) * prev[0];
        const y = this.alpha * lm[1] + (1 - this.alpha) * prev[1];
        const z = this.alpha * lm[2] + (1 - this.alpha) * prev[2];
        
        filtered[i] = [x, y, z, lm[3]] as any;
        this.history.set(key, [x, y, z]);
      } else {
        // First frame, no smoothing
        filtered[i] = lm;
        this.history.set(key, [lm[0], lm[1], lm[2]]);
      }
    }

    return filtered;
  }

  reset() {
    this.history.clear();
  }
}

/**
 * Simple outlier rejection based on frame-to-frame distance
 */
class OutlierFilter {
  private history: Map<string, [number, number, number]> = new Map();
  private maxDelta: number;

  constructor(maxDelta: number = 0.05) {
    this.maxDelta = maxDelta; // Max normalized distance per frame (e.g., 0.05 = 5% of frame)
  }

  filter(landmarks: LandmarksArray): LandmarksArray {
    const filtered: LandmarksArray = [] as any;

    for (let i = 0; i < landmarks.length; i++) {
      const lm = landmarks[i];
      if (!lm || lm[0] === undefined) {
        filtered[i] = lm;
        continue;
      }

      const key = `${i}`;
      const prev = this.history.get(key);

      if (prev) {
        // Calculate Euclidean distance
        const dx = lm[0] - prev[0];
        const dy = lm[1] - prev[1];
        const dz = lm[2] - prev[2];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (dist > this.maxDelta) {
          // Outlier detected, clamp to max delta
          const scale = this.maxDelta / dist;
          const x = prev[0] + dx * scale;
          const y = prev[1] + dy * scale;
          const z = prev[2] + dz * scale;
          
          filtered[i] = [x, y, z, lm[3]] as any;
          this.history.set(key, [x, y, z]);
        } else {
          // Normal movement
          filtered[i] = lm;
          this.history.set(key, [lm[0], lm[1], lm[2]]);
        }
      } else {
        // First frame
        filtered[i] = lm;
        this.history.set(key, [lm[0], lm[1], lm[2]]);
      }
    }

    return filtered;
  }

  reset() {
    this.history.clear();
  }
}

/**
 * Limb length stabilization to reduce accordion effect
 */
class LimbLengthFilter {
  private limbLengths: Map<string, number> = new Map();
  private emaDecay: number;
  private blendFactor: number;

  // MediaPipe Pose limb segments
  private limbs: [number, number][] = [
    [11, 13], // Left upper arm
    [13, 15], // Left forearm
    [12, 14], // Right upper arm
    [14, 16], // Right forearm
    [23, 25], // Left thigh
    [25, 27], // Left shank
    [24, 26], // Right thigh
    [26, 28], // Right shank
  ];

  constructor(emaDecay: number = 0.9, blendFactor: number = 0.5) {
    this.emaDecay = emaDecay;
    this.blendFactor = blendFactor;
  }

  filter(landmarks: LandmarksArray): LandmarksArray {
    const filtered: LandmarksArray = JSON.parse(JSON.stringify(landmarks)); // Deep copy

    for (const [parentIdx, childIdx] of this.limbs) {
      const parent = landmarks[parentIdx];
      const child = landmarks[childIdx];

      if (!parent || !child || parent[0] === undefined || child[0] === undefined) {
        continue;
      }

      // Check visibility
      if (parent[3] < 0.5 || child[3] < 0.5) {
        continue;
      }

      // Calculate current limb length
      const dx = child[0] - parent[0];
      const dy = child[1] - parent[1];
      const dz = child[2] - parent[2];
      const currentLength = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (currentLength === 0) continue;

      const key = `${parentIdx}_${childIdx}`;
      let emaLength = this.limbLengths.get(key);

      if (emaLength === undefined) {
        // First time seeing this limb
        this.limbLengths.set(key, currentLength);
        continue;
      }

      // Update EMA
      emaLength = emaLength * this.emaDecay + currentLength * (1 - this.emaDecay);
      this.limbLengths.set(key, emaLength);

      // Calculate correction
      const targetLength = emaLength;
      const correctionScale = targetLength / currentLength;

      // Apply correction to child joint (blend with original)
      const correctedChild = [
        parent[0] + dx * correctionScale,
        parent[1] + dy * correctionScale,
        parent[2] + dz * correctionScale,
      ];

      // Blend correction
      filtered[childIdx][0] = child[0] + (correctedChild[0] - child[0]) * this.blendFactor;
      filtered[childIdx][1] = child[1] + (correctedChild[1] - child[1]) * this.blendFactor;
      filtered[childIdx][2] = child[2] + (correctedChild[2] - child[2]) * this.blendFactor;
    }

    return filtered;
  }

  reset() {
    this.limbLengths.clear();
  }
}

/**
 * Composite filter that applies all filters in sequence
 */
export class SyncPoseFilter {
  private emaFilter: EMAFilter;
  private outlierFilter: OutlierFilter;
  private limbLengthFilter: LimbLengthFilter;
  private enabled: boolean;

  constructor(config?: {
    emaAlpha?: number;
    outlierMaxDelta?: number;
    limbEmaDecay?: number;
    limbBlendFactor?: number;
    enabled?: boolean;
  }) {
    this.emaFilter = new EMAFilter(config?.emaAlpha ?? 0.3);
    this.outlierFilter = new OutlierFilter(config?.outlierMaxDelta ?? 0.05);
    this.limbLengthFilter = new LimbLengthFilter(
      config?.limbEmaDecay ?? 0.9,
      config?.limbBlendFactor ?? 0.5
    );
    this.enabled = config?.enabled ?? true;
  }

  /**
   * Apply all filters in sequence (synchronous, <1ms)
   */
  filter(landmarks: LandmarksArray): LandmarksArray {
    if (!this.enabled || !landmarks || landmarks.length < 33) {
      return landmarks;
    }

    // Apply filters in order:
    // 1. EMA smoothing (reduces high-frequency jitter)
    let filtered = this.emaFilter.filter(landmarks);

    // 2. Outlier rejection (prevents sudden jumps)
    filtered = this.outlierFilter.filter(filtered);

    // 3. Limb length stabilization (reduces accordion effect)
    filtered = this.limbLengthFilter.filter(filtered);

    return filtered;
  }

  /**
   * Reset all filter state (call on video change)
   */
  reset() {
    this.emaFilter.reset();
    this.outlierFilter.reset();
    this.limbLengthFilter.reset();
  }

  /**
   * Enable/disable filtering at runtime
   */
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }
}

/**
 * Default filter configuration (conservative, safe)
 */
export const DEFAULT_SYNC_FILTER_CONFIG = {
  emaAlpha: 0.3, // More smoothing (0.3 = 70% previous, 30% current)
  outlierMaxDelta: 0.05, // 5% max movement per frame
  limbEmaDecay: 0.9, // 90% previous length, 10% current
  limbBlendFactor: 0.5, // 50% correction blend
  enabled: true,
};

















